import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Inject, Injectable, Optional, PLATFORM_ID, TransferState, DOCUMENT } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { ORIGIN } from '../tokens';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HandledHttpResponse } from '@uzenith360/http-utils';
import Portfolio from './fetch-data-from-db/types/porfolio.interface';
import portfolioDataKey from './fetch-data-from-db/portfolio-data.key';

@Injectable({
  providedIn: 'root'
})
export class PortfolioResolver {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(ORIGIN) @Optional() private readonly origin: string,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly transferState: TransferState,
  ) { }

  private static fetchPortfolioFromDb?: (origin: string) =>
    Promise<HandledHttpResponse<Portfolio>>;

  private async getFetchPortfolioFromDb(): Promise<
    (origin: string) =>
      Promise<HandledHttpResponse<Portfolio>>
  > {
    if (PortfolioResolver.fetchPortfolioFromDb !== undefined) {
      return PortfolioResolver.fetchPortfolioFromDb;
    }

    const { fetchPortfolio } = await import('./fetch-data-from-db');

    return PortfolioResolver.fetchPortfolioFromDb = fetchPortfolio;
  }

  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<{ data?: Portfolio, message?: string | undefined, statusCode: number }> {
    const { message, statusCode, data } = await firstValueFrom(
      await this.fetchPortfolio(this.origin ?? this.document.location.origin)
    );

    if (statusCode === 200 && !!data) {
      return {
        statusCode,
        data,
      };
    } else {
      return { message, statusCode }
    }
  }

  private async fetchPortfolio(origin: string): Promise<Observable<HandledHttpResponse<Portfolio>>> {
    const defaultErrorResponse = { message: 'Problem loading portfolio, please try again', statusCode: 500, data: undefined };

    if (isPlatformServer(this.platformId)) {
      const result: HandledHttpResponse<Portfolio>
        = await (await this.getFetchPortfolioFromDb())(origin)
          .catch(
            (err) => {
              console.error(err);

              return defaultErrorResponse;
            }
          );

      this.transferState.set(portfolioDataKey, result);

      return of(result);
    } else if (isPlatformBrowser(this.platformId)) {
      return of(this.transferState.get(portfolioDataKey, defaultErrorResponse));
    } else {
      console.error('Unexpected platform');

      return of(defaultErrorResponse);
    }
  }
}
